import React, { forwardRef, useEffect, useImperativeHandle, useRef, useMemo, useState } from 'react'
import PropTypes from "prop-types";
import { Accordion, Form, Checkbox } from 'semantic-ui-react'
import CurrencyDropDown from '../shared/CurrencyDropDown';
import CurrencyInput from '../shared/CurrencyInput';
import { Currencies } from '../shared/currencies';
import NumericInput from '../shared/NumericInput';
import { CreditCardAPIPayment, GetFolios, CreditCardAPIPaymentSave } from '../../api/services/finance';
import { GetPaymentProviderCvvSession } from '../../api/services/pms';
import { GetHotels } from '../../api/services/organization';
import { useTextResources, useMessageResources } from '../../language'
import { useLoader } from '../shared/utils';
import SavedCardsList from './savedCardsList';
import AuthorizationsList from './authorizationsList';
import { CreateTokenDialog } from './createTokenDialog';
import moment from 'moment'
import { EntityType, EntityTypes } from '../shared/constants';
import { onError, onWarning } from '../shared/messages';
import _ from 'lodash';
import CreditTypeDropDown from '../shared/CreditTypeDropDown';
import ExternalPayments from '../ExternalPaymentsComponent';
import { GetPinPadCode, ECompanyPayment } from './paymentCommon';
import { CurrencyExchange } from '../../api/services/next';

export const PaymentDialog = forwardRef(({
    reservation,
    HotelId,
    defaultAction,
    availableActions,
    availablePanels,
    amount: initialAmount,
    currencyId,
    readOnlyAmount,
    selectFirst,
    allowedPaymentProviderIDs,
    onAmountChange,
    minVal,
    isSinglePayment,
    onIs3dsOpenChange,
    isReadOnly,
    ...rest }, ref) => {

    const text = useTextResources();
    const message = useMessageResources();
    const [is3DS, setIs3DS] = useState(false);
    const [code3DS, setCode3DS] = useState();
    const [actionType, setActionType] = useState(defaultAction || 1);
    const [currency, setCurrency] = useState(currencyId || reservation && reservation.CurrencyId );
    const [amount, setAmount] = useState(initialAmount || (reservation ? reservation.TotalAmountAfterTax : 1));
    const [payments, setPayments] = useState(1);
    const [selected, setSelected] = useState();
    const [creditType, setCreditType] = useState("Payments");
    const [selectedToken, setSelectedToken] = useState();
    const [folios, setFolios] = useState([]);
    const [panels, setPanels] = useState([]);
    const [entities, setEntities] = useState([]);
    const [showOtherOwnerCards, setShowOtherOwnerCards] = useState(false);
    const loader = useLoader();
    const [entity, setEntity] = useState(null);
    const dialogRef = useRef();
    const cvvInputRef = useRef();
    const { formProps } = rest;
    const [currencyRates, setCurrencyRates] = useState([]);
    const [guestId, setGuestId] = useState();
    const [cvvSession, setCvvSession] = useState();
    const [amountDisable, setAmountDisable] = useState(false);
    const [ccProvider, setCCProvider] = useState();
    const savedCardsListRef = useRef();
    const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1);
    // IsCVVMandatory from HotelParameters - controls if CVV is required for saved cards
    const [isCvvMandatory, setIsCvvMandatory] = useState(false);

    const handleTokenAdded = async (newToken) => {
        try {
            if (newToken && newToken.Id) {
                if (savedCardsListRef.current?.reload) {
                    // Passes the ID directly to the reload to prevent a race condition
                    await savedCardsListRef.current.reload(newToken.Id)
                }
                const savedCardsIndex = panels.findIndex(p => p.key === PaymentDialog.Panels.SavedCards)
                if (savedCardsIndex !== -1) {
                    setActiveAccordionIndex(savedCardsIndex)
                }
            }
        } catch (error) {
            onError({ message: "Failed to reload saved cards: " + error.message })
        }
    }

    useEffect(() => {
        if(reservation) {
            const currentGuestId = reservation.GuestId || null;
            if (currentGuestId !== guestId) {
                setGuestId(currentGuestId);
                const setCvvSessionFromApi = async () => {
                    const session = await GetPaymentProviderCvvSession({ HotelID: HotelId, GuestId : currentGuestId });
                    setCvvSession(session.SessionId);
                    setCCProvider(session.Provider);
                };
                setCvvSessionFromApi();
            }
        }
    }, [reservation?.GuestId, guestId, HotelId]);

    useEffect(() => {
        setAmount(initialAmount || (reservation ? reservation.TotalAmountAfterTax : 1))
        if (reservation?.Id) {
            (async () => {
                try {
                    const folios = await GetFolios({
                        HotelID: HotelId,
                        EntityIDs: [reservation.Id],
                        EntityTypeIDs: [EntityType.Reservation],
                        IncludeOwner: true,
                        IncludeFolioEntities: true,
                    })
                    setFolios(folios)
                } catch (error) {
                    onError(error)
                }
            })()
        }
    }, [initialAmount, reservation.Id, reservation.TotalAmountAfterTax ]);

    useEffect(() => {
      (async () => {
        try {
          const hotels = await GetHotels({
            ID: HotelId,
            IsActive: true,
            IncludeParameters: true,
          });

          if (hotels && hotels.length) {
            const hotel = hotels[0];
            // Set IsCVVMandatory from hotel parameters
            if (hotel.HotelParameter) {
              setIsCvvMandatory(hotel.HotelParameter.IsCVVMandatory || false);
            }
            const currencyRates = await CurrencyExchange.GetHotelCurrencyExchangeRates({
              HotelID: HotelId,
              Date: moment(hotel.HotelParameter.CurrentBusinessDate)
                .startOf('day')
                .toISOString(),
            });
            setCurrencyRates(currencyRates);
          }
        } catch (error) {
          onError(error);
        }
      })();
    }, [HotelId]);

    useEffect(() => {
        renderPanels();
    }, [actionType, availablePanels, entities, selected, showOtherOwnerCards, is3DS, reservation.GuestId, ccProvider]);

    useEffect(() => {
        getEntities();
    }, [folios, entity]);

    let actions = [
        {
            text: text("SaveCreditCard"),
            value: PaymentDialog.Actions.SaveCreditCard
        },
        {
            text: text("TakeAuthorization"),
            value: PaymentDialog.Actions.TakeAuthorization
        },
        {
            text: text("TakePayment"),
            value: PaymentDialog.Actions.TakePayment
        }
    ];

    if (availableActions) {
        actions = actions.filter(x => availableActions.includes(x.value));
    }

    HotelId = reservation ? reservation.HotelId : HotelId;
    const currencySymbol = Currencies[currency].symbol;
    const mapJPaymentType = {
        1: 4,
        2: 2,
        3: 1
    };

    const calculateCurrencyValue = (value, currencyID, targetCurrencyID) => {
        const rate = currencyRates.find(x => x.SourceCurrencyId === currencyID && x.TargetCurrencyId === targetCurrencyID)
        if (rate) {
            return value * rate.PaymentRate
        } else {
            return value
        }
    }

    const setCurrencyId = val => {
        const currentCurrency = currency;
        const targetCurrency = val;
        setCurrency(targetCurrency)
        if (rest.onSetCurrency) {
            rest.onSetCurrency(targetCurrency)
        }

        const newAmount = calculateCurrencyValue(amount, currentCurrency,targetCurrency);
        setAmount(newAmount);
    }

    useEffect(() => {
      if (reservation?.CurrencyId) {
        setCurrencyId(reservation.CurrencyId);
      }
    }, [reservation?.CurrencyId]);
    

    const getFolioId = () => {
        if (reservation && reservation.FolioId) {
            return reservation && reservation.FolioId
        } else if (folios && folios.length) {
            const folio = folios.find(x => x.Owner && x.Owner.EntityTypeId === entity)
            return folio && folio.Id
        } else {
            console.warn("Folio could not be found")
        }
    }
    // This is the token as saved in the DB
    const createCreditGuardResponse = token => {
        return {
            HotelId,
            CompanyPayment: token.CompanyPaymentName,
            PaymentType: mapJPaymentType[actionType], // Authorization
            Cvv: token.Cvv,
            Currency: Currencies[currency].code,
            TotalCharge: amount,
            EntityId: reservation && reservation.Id,
            EntityTypeId: reservation && 1,
            FolioID: getFolioId(),
            Token: token.Token,
            NumberOfPayments: payments,
            PaymentTokenID: token.Id,
            CreditType: payments > 1 && creditType,
            CardExpDate: moment(token.CreditCardExpirationDate).format("MM/YYYY"),
            Language: 3,  // EN
            ExtEntity: entities.map(e => ({ EntityTypeID: e.type, EntityId: e.id })),
            ProviderTransactionIdentifier: token.ProviderTransactionIdentifier,
            CreditCardTypeId: token.CreditCardTypeID
        }
    }

    // This is the token as returned from savetoken call
    const createCreditGuardResponse2 = token => {
        //const entity = token.PaymentTokenEntities.find(x => x.EntityTypeID !== 1)
        return {
            HotelId,
            CompanyPayment: token.PaymentProviderID,
            PaymentType: mapJPaymentType[actionType], // Authorization
            Cvv: token.Code,
            Currency: Currencies[currency].code,
            TotalCharge: amount,
            EntityId: token.EntityID || (reservation && reservation.Id),
            EntityTypeId: token.EntityTypeID || (reservation && 1),
            NumberOfPayments: payments,
            Token: token.Token,
            FolioID: getFolioId(),
            PaymentTokenID: token.Id,
            CardExpDate: moment(token.CreditCardExpirationDate).format("MM/YYYY"),
            Language: 3,  // EN
            CreditType: payments > 1 && creditType,
            ExtEntity: (token.PaymentTokenEntities || entities).map((e) => ({
                EntityTypeID: e.type || e.EntityTypeID,
                EntityId: e.id || e.EntityID,
              })),
            CardCompanyName: token.CardCompanyName,
            LastFourDigits: token.LastFourDigits,
            CompanyPaymentName: token.CompanyPaymentName,            
            CreditCardTypeId: token.CreditCardTypeID
        }
    }

    const setExternalPayment = (externalPayment) => {
        const token = {
            HotelId: reservation.HotelId,
            Currency: Currencies[currency].code,
            EntityId: reservation && reservation.Id,
            EntityTypeId: reservation && 1,
            FolioID: getFolioId(),
            TotalCharge: amount,
            PaymentType: 1,
            Language: 3, //EN
            ExtEntity: entities.map(e => { return { EntityID: e.id, EntityTypeID: e.type } }),
            CompanyPayment: externalPayment.name,
            Track2: GetPinPadCode(externalPayment.id),
            IsExternalPayment: true,
            CreditType: payments > 1 && creditType,
            NumberOfPayments: payments
        }
        setSelectedTokenSimple(token);
    }

    /**
     * handler 3ds iframe post message
     * set 3ds iframe 
     * and listen to post message events
     * @param {*} event 
     */
    const perform3ds = async (token, result, resolve, reject) => {
        setCode3DS(result.PathUrl);
        if(onIs3dsOpenChange) {
            onIs3dsOpenChange(true);
        }
        loader(false);

        token.ProviderTransactionIdentifier = result.TransactionId;
        token.Uid = result.GuidReferrence;
        token.Is3DSResponse = true;
     
        const _3dsHandler = async event => {
            
            if(event && event.data && event.data.event == '[bp]:deposit') {
                window.removeEventListener("message", _3dsHandler);
                if(event.data.type == 'approved') {
                    switch (actionType) {
                        case PaymentDialog.Actions.TakePayment:
                            loader(true);
                            result = await CreditCardAPIPayment(token);
                            loader(false);
                            if (!result.ErrorCode) {
                                resolve(result);
                            } else {
                                setCode3DS(null);

                                if(onIs3dsOpenChange) {
                                    onIs3dsOpenChange(false);
                                }
                                reject({
                                    ErrorMessage: result.ErrorText
                                });
                            }
                            break;
                        case PaymentDialog.Actions.TakeAuthorization:
                        default:
                            loader(true);
                            result = await CreditCardAPIPaymentSave(token);
                            loader(false);
                            if (!result.ErrorCode) {
                                resolve(result);
                            } else {
                                setCode3DS(null);
                                if(onIs3dsOpenChange) {
                                    onIs3dsOpenChange(false);
                                }
                                reject({
                                    ErrorMessage: result.ErrorText
                                });
                            }
                            break;
                    }
                } else {
                    setCode3DS(null);
                    if(onIs3dsOpenChange) {
                        onIs3dsOpenChange(false);
                    }
                    reject({
                        ErrorMessage: text("_3DSdecline")
                    });
                }
            }
        };
        window.addEventListener("message", _3dsHandler);
    }
    
    const performSaveAction = async (token, parameters, resolve, reject) => {
        try {
            loader(true);
            let haveCvvFromIframe = false;
            if (token) {
                if (!token.IsExternalPayment) {
                    token = createCreditGuardResponse(token)
                }
            }

            else if (dialogRef.current) {
                const tokenResult = await dialogRef.current.saveAction(parameters);
                if (tokenResult) {
                    token = createCreditGuardResponse2(tokenResult);
                    haveCvvFromIframe = true;
                }
            }


            if (token && actionType != PaymentDialog.Actions.SaveCreditCard) {
                if (!token.FolioID) {
                    token.FolioID = parameters.folioId
                }
                
                // Handle CVV for 3DS (BridgerPay)
                if(is3DS) {
                    // have not before cvv and have cvvIframe
                    // get data from iframe
                    if (!token._3DS && !haveCvvFromIframe && cvvInputRef.current) {
                        const cvv = await cvvInputRef.current.getCvv(parameters);
                        token.Cvv = cvv;
                    } 

                    // in 3D Security validate have cvv
                    if(!token.Cvv) {
                        reject({
                            ErrorMessage: text("NoCvvDataTo3dSecurity"),
                            status: 400
                        });
                        return;
                    }
                }
                
                // Handle CVV for Credit Guard saved cards (simple input)
                if (ccProvider !== ECompanyPayment.BRIDGEPAY && !haveCvvFromIframe && cvvInputRef.current) {
                    const cvv = cvvInputRef.current.getCvv();
                    if (cvv) {
                        token.Cvv = cvv;
                    } else if (isCvvMandatory) {
                        // If CVV is mandatory and not provided, reject
                        reject({
                            ErrorMessage: text("NoCvvDataTo3dSecurity"),
                            status: 400
                        });
                        return;
                    }
                }

                token._3DS = is3DS;
                if(!token.Currency) {
                    token.Currency = currency;
                }

                let result = await CreditCardAPIPayment(token);
                if (result.ErrorCode) {
                    if(result.ErrorCode == "RedirectUrl") {
                        await perform3ds(token, result, resolve, reject);
                    } else {
                        reject({
                            ErrorMessage: result.ErrorText
                        });
                    }
                } else {
                    resolve(result)
                }
            } else {
                resolve(token);
            }
        } catch (error) {
            reject(error)
        } finally {
            loader(false)
        }
    }

    useImperativeHandle(ref, () => ({
        saveAction: (parameters) => {
            return new Promise((resolve, reject) => {
                performSaveAction(selectedToken, parameters, resolve, reject)
            })
        },
        clearSelection: () => {
            setSelectedToken(null);
            setSelected(null);
            setActiveAccordionIndex(-1);
        }
    }))

    const getEntities = () => {
        const result = []
        if (reservation && reservation.Id) {
            result.push({ type: EntityType.Reservation, id: reservation.Id })
            if (folios) {
                const owner = folios.map(x => x.Owner).find(x => x.EntityTypeId === entity)
                if (owner) {
                    result.push({ type: owner.EntityTypeId, id: owner.EntityId })
                }
            }
        } else if (reservation.entities) {
            reservation.entities.forEach(x => {
                result.push({
                    type: x.EntityTypeId,
                    id: x.EntityId
                })
            })
            result.push({ type: EntityType.Folio, id: reservation.FolioId })
        }
        setEntities(result)
    }

    const availableEntities = useMemo(() => {
        let result = []
        if (folios && folios.length > 0) {
            const etypes = _.uniq(folios.map(x => x.Owner.EntityTypeId))
            result = etypes.map(t => ({
                text: text(EntityTypes[t]),
                value: t
            }))
        } else if (reservation?.entities && reservation.entities.length > 0) {
            const folioOwners = reservation.entities.filter(x => x.IsFolioOwner === true);
            const etypes = _.uniq(folioOwners.map(x => x.EntityTypeId))
            result = etypes.map(t => ({
                text: text(EntityTypes[t]),
                value: t
            }))
        }
        return result
    }, [folios, reservation?.entities]);

    // Set default entity to the first available entity if not already set
    useEffect(() => {
        if (availableEntities.length > 0 && entity === null) {
            setEntity(availableEntities[0].value);
        }
    }, [availableEntities, entity]);

    const setSelectedAutorisationToken = (token)=> {
        setSelectedToken(token);
        if(ccProvider == ECompanyPayment.BRIDGEPAY) {
            setAmountDisable(true);
            setAmount(token.Amount)
            if (onAmountChange) onAmountChange(token.Amount)
        }
    };

    const setSelectedTokenSimple = (token)=> {
        setSelectedToken(token);
        if(ccProvider == ECompanyPayment.BRIDGEPAY) {
            setAmountDisable(false);
        }
    };

    const renderPanels = () => {
        const panels = []
        if (!availablePanels || availablePanels.includes(PaymentDialog.Panels.SavedCards)) {
            panels.push({
                key: PaymentDialog.Panels.SavedCards,
                title: {
                    content: text("SavedCards"),
                    id: "SavedCards"
                },
                content: {
                    style: { maxHeight: 260, overflowX: "auto" },
                    content:
                            <SavedCardsList
                                ref={savedCardsListRef}
                                cvvRef={cvvInputRef}
                                reservation={reservation}
                                hotelid={HotelId}
                                onSelect={setSelectedTokenSimple}
                                onSelectItem={setSelectedTokenSimple}
                                selectFirst={selectFirst}
                                is3DS={is3DS}
                                cvvSession={cvvSession}
                                guestId={guestId}
                                allowedPaymentProviderIDs={allowedPaymentProviderIDs}
                                onLoadListFinished={onLoadListFinished}
                                getEntitiesByUser={showOtherOwnerCards}
                                isCvvMandatory={isCvvMandatory} />,
                    id: "SavedCards-content"
                }
            })
        }

        if (actionType !== PaymentDialog.Actions.SaveCreditCard && (!availablePanels || availablePanels.includes(PaymentDialog.Panels.SavedAuthorizations))) {
            panels.push({
              key: PaymentDialog.Panels.SavedAuthorizations,
              title: {
                content: text('SavedAuthorizations'),
                id: 'SavedAuthorizations',
              },
              content: {
                content: (
                    <AuthorizationsList
                      reservation={reservation}
                      hotelid={HotelId}
                      authorization
                      onSelect={setSelected}
                      onSelectItem={setSelectedAutorisationToken}
                      filterUsed
                      is3DS={is3DS}
                      selected={selected}
                      onLoadListFinished={onLoadListFinished}
                      showAllRelatedCards={showOtherOwnerCards}
                    />
                ),
                id: 'SavedAuthorizations-content',
              },
            });
        }

        if (actionType === PaymentDialog.Actions.TakePayment && (!availablePanels || availablePanels.includes(PaymentDialog.Panels.ExternalPayments))) {
            panels.push({
                title: {
                    content: text("ExternalPayments"),
                    id: "ExternalPayments"
                },
                key: PaymentDialog.Panels.ExternalPayments,
                content: {
                    content: <ExternalPayments onSelectItem={value => setExternalPayment(value)} />
                }
            })
        }

        if (!availablePanels || availablePanels.includes(PaymentDialog.Panels.AddCreditCard)) {
            panels.push({
                key: PaymentDialog.Panels.AddCreditCard,
                title: {
                    content: text("AddCreditCard"),
                    id: "AddCreditCard"
                },
                content: {
                    content: <div>
                        {(availableEntities.length !== 0) ? (
                            ccProvider !== ECompanyPayment.BRIDGEPAY ? (
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10}}>
                                <div>
                                    <Form.Field>
                                        <Form.Select label={text("Entity")} options={availableEntities} value={entity} onChange={(e, { value }) => setEntity(value)} />
                                    </Form.Field>
                                </div>
                                <div>
                                    <CreateTokenDialog ref={dialogRef} HotelId={HotelId} entities={entities} guestId={guestId} onTokenAdded={handleTokenAdded} ccProvider={ccProvider} {...formProps}></CreateTokenDialog>
                                </div>
                            </div>
                            ) : (
                                <div>
                                    <Form.Field>
                                        <Form.Select label={text("Entity")} options={availableEntities} value={entity} onChange={(e, { value }) => setEntity(value)} />
                                        </Form.Field>
                                        <CreateTokenDialog ref={dialogRef} HotelId={HotelId} entities={entities} guestId={guestId} onTokenAdded={handleTokenAdded} ccProvider={ccProvider} {...formProps}></CreateTokenDialog>
                                </div>
                            )
                        ) : (<div></div>)}
                    </div>
                }
            })
        }
        setPanels(panels);
    }

    const onLoadListFinished = (panelId, noOfItems) => {
        const title = document.getElementById(panelId);
        if (!title) return;

        if (title.innerText.includes(' (')) {
            const split = title.innerText.split(' (');
            title.innerHTML = title.innerHTML.replace(title.innerText, split[0] + " (" + noOfItems + ")")
        } else {
            title.innerHTML = title.innerHTML.replace(title.innerText, title.innerText + " (" + noOfItems + ")")
        }
    }

    const updateAmount = newAmount => {
        if (newAmount < 0) return onWarning(message("RefundNotPermittedForLoggedInUser"));
        setAmount(newAmount)
        if (selectedToken) {
            setSelectedTokenSimple({ ...selectedToken, TotalCharge: newAmount })
        }
        if (onAmountChange) onAmountChange(newAmount)
    }
    const show3dsAction = (actionType === PaymentDialog.Actions.TakePayment || actionType === PaymentDialog.Actions.TakeAuthorization) && 
        (!amount || amount >= 0);
    const support3dsProvider = (ccProvider == ECompanyPayment.BRIDGEPAY);
    const show3ds = show3dsAction && support3dsProvider;

    // in code3DS is end step that user only input 3ds code 
    if (code3DS) {
        return <iframe src={code3DS} className='security-iframe'></iframe>;
    } else {
       return <Form {...rest} style={isReadOnly ? { opacity: 0.6, pointerEvents: 'none' } : {}} >
            {!defaultAction && <Form.Select disabled={isReadOnly} label={text("ActionType")} value={actionType} onChange={(e, { value }) => setActionType(value)} options={actions}></Form.Select>}
            {actionType !== PaymentDialog.Actions.SaveCreditCard && <Form.Group widths="2">
                <Form.Field
                    label={text("Amount")}
                    disabled={readOnlyAmount || amountDisable || isReadOnly}
                    value={amount || 0}
                    min={minVal || 0}
                    onChange={newAmount => updateAmount(newAmount)}
                    control={CurrencyInput}
                    currency={currencySymbol}
                />
                <Form.Field disabled={isReadOnly} label={text("Currency")} control={CurrencyDropDown} textField="Name" descriptionField="Symbol" value={currency} onChange={setCurrencyId}></Form.Field>
            </Form.Group>}
            {actionType === PaymentDialog.Actions.TakePayment && !isSinglePayment && <Form.Group widths="2">
                <Form.Field disabled={ccProvider == ECompanyPayment.BRIDGEPAY || isReadOnly}  label={text("NumberOfPayments")} value={payments} onChange={setPayments} control={NumericInput} min={1} max={12} digits={0}></Form.Field>
                <Form.Field disabled={payments === 1 || isReadOnly} label={text("CreditType")} value={creditType} onChange={setCreditType} control={CreditTypeDropDown}></Form.Field>
            </Form.Group>}
            <div className='checkbox-container'>
                <Checkbox
                    disabled={isReadOnly}
                    onChange={(e, data) =>
                    setShowOtherOwnerCards(data.checked)
                    }
                    label={text('ShowAllRelatedCards')}
                />            
                {
                    show3ds &&
                    <Checkbox
                        disabled={isReadOnly}
                        toggle
                        label={text('_3dSecure')}
                        checked={is3DS}
                        onChange={(e, { checked }) => setIs3DS(checked)}
                    />
                }
            </div>
            <Accordion
                style={{ maxHeight: 400, overflowX: "none" }}
                activeIndex={activeAccordionIndex}
                onTitleClick={(e, titleProps) => {
                    if (!isReadOnly) {
                        const { index } = titleProps
                        const newIndex = activeAccordionIndex === index ? -1 : index
                        setActiveAccordionIndex(newIndex)
                        setSelected(null)
                        setSelectedTokenSimple(null)
                    }
                }}
                panels={panels} />
                {isReadOnly && (
                <div style={{ 
                    marginTop: '10px', 
                    padding: '10px', 
                    backgroundColor: '#f9f9f9', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '4px',
                    color: '#666',
                    fontSize: '14px',
                    textAlign: 'center'
                }}>
                    {text("PaymentDialogReadOnlyMessage")}
                </div>
            )}
        </Form>
    }
})



PaymentDialog.displayName = 'PaymentDialog'
PaymentDialog.propTypes = {
    currencyId: PropTypes.any,
    reservation: PropTypes.any,
    HotelId: PropTypes.any,
    type: PropTypes.any,
    defaultAction: PropTypes.any,
    availableActions: PropTypes.any,
    amount: PropTypes.number,
    selectFirst: PropTypes.bool,
    availablePanels: PropTypes.array,
    allowedPaymentProviderIDs: PropTypes.array,
    onAmountChange: PropTypes.func,
    readOnlyAmount: PropTypes.bool,
    minVal: PropTypes.number,
    isSinglePayment: PropTypes.bool,
    onIs3dsOpenChange: PropTypes.func,
    isReadOnly: PropTypes.bool,
}

PaymentDialog.Panels = {
    SavedCards: 1,
    SavedAuthorizations: 2,
    ExternalPayments: 3,
    AddCreditCard: 4
}

PaymentDialog.Actions = {
    SaveCreditCard: 1,
    TakeAuthorization: 2,
    TakePayment: 3
}